import React, { useState } from 'react';
import { BackButton, TextButton as Button, ComponentM, ComponentTextStyle, InputLabel, Size, TextField } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GrayBox, StepContainer, StepDescription, StepTitle } from './StepComponents';
import BreadcrumbTopSection from '../../../../commonComponents/BreadcrumbTopSection';
import { selectMarket, buildMarketPath } from '../../../../store/slices/globalConfigSlice';
import posthog from 'posthog-js';

interface Step7Props {
  onNext: () => void;
  onPrevious: () => void;
  handleResolvedStep: () => void;
  setFirmwareVersion: (version: string) => void;
}

const Step7: React.FC<Step7Props> = ({ onNext, onPrevious, handleResolvedStep, setFirmwareVersion }) => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const [version, setVersion] = useState('');

  return (
    <StepContainer>
      <BreadcrumbTopSection
        setPadding={false}
        breadcrumb={[
          { text: t('Home', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/') },
          { text: t('Software and Apps', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps') },
          { text: t('QCPR App', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps/product/qcpr-app') },
          { text: t('Troubleshooting', { ns: 'qcprSupport' }) }
        ]}
      />

      <BackButton onClick={onPrevious} size={Size.Small}>
        {t('Back', { ns: 'qcprSupport' })}
      </BackButton>

      <StepTitle>{t(`What is your manikin's firmware version?`, { ns: 'qcprSupport' })}</StepTitle>
      <StepDescription>{t('Outdated firmware can prevent proper connection to the app.', { ns: 'qcprSupport' })}</StepDescription>

      <InputLabel inputId="firmwareVersion" text={t('Enter firmware version if known:', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="firmwareVersion"
        data-testId="firmwareVersion"
        placeholder={t('Eg: 0.000', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={version}
        onChange={(value) => setVersion(value)}
      />

      <GrayBox>
        <ComponentM textStyle={ComponentTextStyle.Bold}>
          {t("Here's how to check", { ns: 'qcprSupport' })}
        </ComponentM>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('How to find firmware version:', { ns: 'qcprSupport' })}
        </ComponentM>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('Open the QCPR app', { ns: 'qcprSupport' })}
        </ComponentM>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('Connect to your manikin', { ns: 'qcprSupport' })}
        </ComponentM>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('Go to Manikin/Device Info', { ns: 'qcprSupport' })}
        </ComponentM>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('Look for "Firmware Version", "BLE" or “Main” (for Resusci manikins)')}
        </ComponentM>

        <img src="/assets/qcprFirmwareVersion.png" style={{ maxWidth: '100%', height: 'auto' }} alt="QCPR Firmware Version" />
      </GrayBox>

      <Button
        size={Size.Large}
        variant='secondary'
        onClick={() => {
          posthog.capture('SUPP-QcprSupport-FirmwareVersionProvided', {
            firmware_version: version,
            step: 7
          });
          setFirmwareVersion(version);
          onNext();
        }}>
        {t("Proceed", { ns: 'qcprSupport' })}
      </Button>

      <StepDescription>{t('Your answers will help our team to assist you.', { ns: 'qcprSupport' })}</StepDescription>
    </StepContainer>
  );
};

export default Step7;
